import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { runTenantStorageSmokeTest } from '../api/providersApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { StatusBadge } from './StatusBadge';

function resolveSmokeMessage(result, t) {
  const rawKey = result?.message_key;
  if (!rawKey) {
    return result?.message || '';
  }
  if (String(rawKey).startsWith('storage.')) {
    return t(`storage:${String(rawKey).slice('storage.'.length)}`, {
      defaultValue: result.message || rawKey,
    });
  }
  return t(rawKey, { defaultValue: result.message || rawKey });
}

export function TenantStorageSmokeTestCard({ tenantId }) {
  const { t } = useTranslation(['storage', 'common']);
  const { session } = useAuth();
  const canRun = hasPermission(session, 'system.tenants.update')
    || hasPermission(session, 'system.providers.manage')
    || hasPermission(session, 'system.providers.health_check');

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const data = await runTenantStorageSmokeTest(tenantId);
      setResult(data);
    } catch (err) {
      const payload = err?.response?.data?.data || err?.response?.data || {};
      if (payload.status) {
        setResult(payload);
      } else {
        setError(resolveSmokeMessage(payload, t) || t('storage:messages.tenantSmokeTestFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!canRun) {
    return null;
  }

  const severity = result?.status === 'ok'
    ? 'success'
    : result?.status === 'skipped'
      ? 'warning'
      : result
        ? 'error'
        : 'info';

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t('storage:labels.smokeTest')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('storage:help.smokeTest')}
      </Typography>
      <Button variant="contained" disabled={busy} onClick={run}>
        {busy ? <CircularProgress size={18} color="inherit" /> : t('storage:actions.runSmokeTest')}
      </Button>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {result && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={severity} sx={{ mb: 1 }}>
            {resolveSmokeMessage(result, t)}
          </Alert>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="body2">
              {t('storage:labels.status')}: <StatusBadge status={result.status} />
            </Typography>
            {result.path && (
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {t('storage:labels.path')}: {result.path}
              </Typography>
            )}
            <Typography variant="body2">
              {t('storage:labels.visibility')}: {result.visibility || 'private'}
            </Typography>
            <Typography variant="body2">
              {t('storage:labels.aclUsed')}: {result.acl_used ? t('common:yes') : t('common:no')}
            </Typography>
            {result.failed_step && (
              <Typography variant="body2">
                {t('storage:labels.failedStep')}: {t(`storage:steps.${result.failed_step}`, { defaultValue: result.failed_step })}
              </Typography>
            )}
          </Stack>
          <List dense>
            {(result.steps || []).map((step) => (
              <ListItem key={step.step} disableGutters>
                <ListItemText
                  primary={`${t(`storage:steps.${step.step}`, { defaultValue: step.step })} — ${step.status}`}
                  primaryTypographyProps={{ fontFamily: 'monospace', fontSize: 14 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
}
